import { customColors } from "../lib/colors";
import animColor from "../lottie/spinnerColor";
import animColorLG from "../lottie/spinnerColorLG";
import animColorXS from "../lottie/spinnerColorXS";
import animDark from "../lottie/spinnerDark";
import animDarkLG from "../lottie/spinnerDarkLG";
import animDarkXS from "../lottie/spinnerDarkXS";
import animLight from "../lottie/spinnerLight";
import animLightLG from "../lottie/spinnerLightLG";
import animLightXS from "../lottie/spinnerLightXS";
import Lottie from "lottie-react";
import React from "react";
const SPINNER_SIZES = ["xs", "sm", "md", "lg", "xl", "2xl"];
// Generate all possible color-shade combinations
const colorVariants = Object.entries(customColors).flatMap(([color, shades]) => Object.keys(shades).map((shade) => `${color}${shade}`));
const SPINNER_VARIANTS = ["color", ...colorVariants];
const pxSizeClasses = {
    xs: "16",
    sm: "20",
    md: "24",
    lg: "32",
    xl: "128",
    "2xl": "192",
};
// Convert hex to RGB array [r, g, b, a]
const hexToRgba = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return [r, g, b, 1];
};
const colors = {
    ...Object.fromEntries(colorVariants.map((variant) => {
        var _a, _b;
        const color = (_a = variant.match(/[a-z]+/)) === null || _a === void 0 ? void 0 : _a[0];
        const shade = (_b = variant.match(/\d+/)) === null || _b === void 0 ? void 0 : _b[0];
        return [variant, hexToRgba(customColors[color][shade])];
    })),
};
const isColorArray = (arr) => {
    return (Array.isArray(arr) &&
        arr.length === 4 &&
        arr.every((n) => typeof n === "number"));
};
// Due to the dynamic nature of Lottie, we use 'any' for the input object.
// This function recursively replaces color arrays within the Lottie animation object.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const replaceColors = (obj, newColor) => {
    if (Array.isArray(obj)) {
        return obj.map((item) => replaceColors(item, newColor));
    }
    else if (obj !== null && typeof obj === "object") {
        for (const key in obj) {
            if (isColorArray(obj[key])) {
                obj[key] = newColor;
            }
            else {
                obj[key] = replaceColors(obj[key], newColor);
            }
        }
    }
    return obj;
};
const Spinner = ({ size = "md", variant = "mono" }) => {
    const fullSize = parseInt(pxSizeClasses[size], 10);
    // Handle custom color variants
    if (variant !== "revert" &&
        variant !== "mono" &&
        variant !== "color" &&
        variant !== "light" &&
        variant !== "dark") {
        let anim;
        switch (size) {
            case "xs":
            case "sm":
            case "md":
                anim = animLightXS;
                break;
            case "xl":
            case "2xl":
                anim = animLightLG;
                break;
            default:
                anim = animLight;
        }
        const animationData = replaceColors(JSON.parse(JSON.stringify(anim)), colors[variant]);
        return (React.createElement(Lottie, { animationData: animationData, style: { width: `${fullSize}px`, height: `${fullSize}px` }, loop: true, autoplay: true }));
    }
    // Handle color variant
    if (variant === "color") {
        let anim;
        switch (size) {
            case "xs":
            case "sm":
            case "md":
                anim = animColorXS;
                break;
            case "xl":
            case "2xl":
                anim = animColorLG;
                break;
            default:
                anim = animColor;
        }
        return (React.createElement(Lottie, { animationData: anim, style: { width: `${fullSize}px`, height: `${fullSize}px` }, loop: true, autoplay: true }));
    }
    if (variant === "light") {
        let anim;
        switch (size) {
            case "xs":
            case "sm":
            case "md":
                anim = animLightXS;
                break;
            case "xl":
            case "2xl":
                anim = animLightLG;
                break;
            default:
                anim = animLight;
        }
        return (React.createElement(Lottie, { animationData: anim, style: { width: `${fullSize}px`, height: `${fullSize}px` }, loop: true, autoplay: true }));
    }
    if (variant === "dark") {
        let anim;
        switch (size) {
            case "xs":
            case "sm":
            case "md":
                anim = animDarkXS;
                break;
            case "xl":
            case "2xl":
                anim = animDarkLG;
                break;
            default:
                anim = animDark;
        }
        return (React.createElement(Lottie, { animationData: anim, style: { width: `${fullSize}px`, height: `${fullSize}px` }, loop: true, autoplay: true }));
    }
    // Handle mono variant (default)
    let lightAnim;
    let darkAnim;
    switch (size) {
        case "xs":
        case "sm":
        case "md":
            lightAnim = animLightXS;
            darkAnim = animDarkXS;
            break;
        case "xl":
        case "2xl":
            lightAnim = animLightLG;
            darkAnim = animDarkLG;
            break;
        default:
            lightAnim = animLight;
            darkAnim = animDark;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Lottie, { animationData: variant && variant === "mono" ? darkAnim : lightAnim, className: "s-block dark:s-hidden", style: { width: `${fullSize}px`, height: `${fullSize}px` }, loop: true, autoplay: true }),
        React.createElement(Lottie, { animationData: variant && variant === "mono" ? lightAnim : darkAnim, className: "s-hidden dark:s-block", style: { width: `${fullSize}px`, height: `${fullSize}px` }, loop: true, autoplay: true })));
};
export { Spinner };
//# sourceMappingURL=Spinner.js.map